import { isSharedDemoMode } from '../config/environment';
import { fetchSharedDemoState, saveSharedDemoState } from './sharedDemoStateService';
import {
    DEFAULT_ADVANCE_LOAN_ENTRY_CATEGORIES,
    LOAN_ENTRY_CATEGORY,
    LOAN_SUBJECT_TYPE,
    REPAYMENT_CATEGORY,
    getLoanEntrySubjectType,
    getActiveLoanEntries,
    getLoanFinancialSummary,
    isRentalLoanEntry,
    normalizeLoanEntryCategory,
    normalizeLoanSubjectType
} from '../constants/loanCategories';

const STORAGE_KEY = 'talentapp_demo_static_state_v1';
const DEMO_DATA_VERSION = 6;
const TOKEN_STORAGE_KEY = 'talentapp_demo_access_token';
const REFRESH_STORAGE_KEY = 'talentapp_demo_refresh_token';
const DEMO_PASSWORD = 'demo1234';

const demoUsers = [
    { id: 'demo-user-super-admin', username: 'demo_admin', role: 'SuperAdmin' },
    { id: 'demo-user-admin', username: 'demo_manager', role: 'Admin' },
    { id: 'demo-user-sales-a', username: 'demo_sales', role: 'Sales' },
    { id: 'demo-user-sales-b', username: 'demo_sales_osaka', role: 'Sales' },
    { id: 'demo-user-residence', username: 'demo_residence', role: 'Residence' },
    { id: 'demo-user-accounting', username: 'demo_accounting', role: 'Accounting' },
];

const clone = (value) => (
    typeof structuredClone === 'function'
        ? structuredClone(value)
        : JSON.parse(JSON.stringify(value))
);

const delay = (value, ms = 120) => (
    new Promise((resolve) => {
        window.setTimeout(() => resolve(clone(value)), ms);
    })
);

const createId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeCategoryNameForStorage = (name) => String(name ?? '').trim().toUpperCase();

const createDefaultLoanEntryCategories = () => (
    DEFAULT_ADVANCE_LOAN_ENTRY_CATEGORIES.map((name, index) => ({
        id: `demo-loan-entry-category-${index + 1}`,
        name,
        normalizedName: normalizeCategoryNameForStorage(name),
        isDefault: true,
        isActive: true,
        sortOrder: index + 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    }))
);

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next.toISOString().slice(0, 10);
};

const addMonths = (date, months) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next.toISOString().slice(0, 10);
};

const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};
const getSubjectKey = (subjectType) => (
    normalizeLoanSubjectType(subjectType) === LOAN_SUBJECT_TYPE.CORPORATE
        ? 'corporate'
        : 'individual'
);

const cloneLoanWithActiveEntries = (loan) => (
    loan ? { ...loan, loanEntries: getActiveLoanEntries(loan) } : loan
);

const recalculateDemoLoanAggregates = (loan) => {
    if (!loan) return;
    const entries = getActiveLoanEntries(loan);
    const totalAmount = entries.reduce((sum, entry) => sum + toNumber(entry.amount), 0);
    const summary = getLoanFinancialSummary({ ...loan, loanEntries: entries });
    loan.totalAmount = totalAmount;
    loan.rentalLoanAmount = summary.rentalBorrowed;
    loan.advanceLoanAmount = summary.advanceBorrowed;
    loan.rentalRepaidAmount = summary.rentalRepaid;
    loan.advanceRepaidAmount = summary.advanceRepaid;
    loan.rentalRemainingBalance = summary.rentalRemaining;
    loan.advanceRemainingBalance = summary.advanceRemaining;
    loan.remainingBalance = summary.totalRemaining;
    loan.loanDate = entries.map(entry => entry.loanDate).filter(Boolean).sort()[0] || loan.loanDate;
    loan.repaymentStartDate = loan.loanDate ? addMonths(loan.loanDate, 1) : loan.repaymentStartDate;
    loan.repaymentEndDate = entries
        .map(entry => entry.plannedEndDate)
        .filter(Boolean)
        .sort()
        .slice(-1)[0] || loan.repaymentEndDate;
};

const getDemoLoanCategoryAmounts = (loan) => {
    const entries = getActiveLoanEntries(loan);
    const rental = entries
        .filter(isRentalLoanEntry)
        .reduce((sum, entry) => sum + toNumber(entry.amount), 0);
    const advance = entries
        .filter(entry => !isRentalLoanEntry(entry))
        .reduce((sum, entry) => sum + toNumber(entry.amount), 0);

    return { rental, advance };
};

const syncTalentLoanAggregates = (draft, loan) => {
    const talent = draft?.talents?.find(item => item.id === loan?.talentId);
    if (!talent || !loan) return;
    const { rental, advance } = getDemoLoanCategoryAmounts(loan);
    const summary = getLoanFinancialSummary(loan);
    talent.activeLoanBorrowedAmount = toNumber(loan.totalAmount);
    talent.activeLoanRentalAmount = rental;
    talent.activeLoanAdvanceAmount = advance;
    talent.activeLoanRentalRepaidAmount = summary.rentalRepaid;
    talent.activeLoanAdvanceRepaidAmount = summary.advanceRepaid;
    talent.activeLoanRepaidAmount = summary.totalRepaid;
    talent.activeLoanRemainingAmount = summary.totalRemaining;
    talent.hasLoanData = true;
    talent.isLoanFullyRepaid = talent.activeLoanRemainingAmount <= 0;
};

const currentMonth = () => new Date().toISOString().slice(0, 7);

const normalizeResidenceStatus = (value) => {
    if (!value) return '';
    return String(value)
        .trim()
        .replace(/\s/g, '')
        .replace(/２/g, '2')
        .replace(/１/g, '1');
};

const isSpecifiedSkill2Status = (value) => normalizeResidenceStatus(value) === '特定技能2号';

const toTime = (value) => {
    if (!value) return 0;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
};

const getCurrentResidenceStatusFromEntries = (mainStatus, renewalEntries = []) => {
    const latestRenewal = (Array.isArray(renewalEntries) ? renewalEntries : [])
        .map((entry, index) => ({ entry, index }))
        .filter(({ entry }) => entry?.residenceStatus && String(entry.residenceStatus).trim() !== '')
        .sort((a, b) => {
            const effectiveDiff =
                toTime(b.entry.renewalGrantDate || b.entry.renewalDate) -
                toTime(a.entry.renewalGrantDate || a.entry.renewalDate);
            if (effectiveDiff !== 0) return effectiveDiff;

            const createdDiff = toTime(b.entry.createdAt) - toTime(a.entry.createdAt);
            if (createdDiff !== 0) return createdDiff;

            return b.index - a.index;
        })[0]?.entry;

    return latestRenewal?.residenceStatus || mainStatus || '';
};

const getCurrentResidenceStatus = (residencePermit) => (
    getCurrentResidenceStatusFromEntries(
        residencePermit?.residenceStatus,
        residencePermit?.renewalEntries
    )
);

const isSpecifiedSkill2Talent = (talent) => (
    isSpecifiedSkill2Status(
        talent?.currentResidenceStatus ||
        getCurrentResidenceStatus(talent?.residencePermit || talent?.residence)
    )
);

const hasRegisteredIntroductionFee = (talent) => (
    talent?.introductionFee !== null &&
    talent?.introductionFee !== undefined &&
    talent?.introductionFee !== ''
);

const decorateTalent = (talent) => {
    if (!talent) return talent;
    const residencePermit = talent.residencePermit || talent.residence || null;
    const currentResidenceStatus = getCurrentResidenceStatus(residencePermit);
    return {
        ...talent,
        residencePermit,
        currentResidenceStatus,
        isSpecifiedSkill2: isSpecifiedSkill2Status(currentResidenceStatus),
        introductionFee: talent.introductionFee ?? null,
    };
};

const decorateTalents = (talents) => talents.map(decorateTalent);

const buildSpecifiedSkill2Stats = (sourceTalents) => {
    const talents = sourceTalents.filter(isSpecifiedSkill2Talent);
    const registeredTalents = talents.filter(hasRegisteredIntroductionFee);
    const totalIntroductionFee = registeredTalents.reduce((sum, talent) => sum + Number(talent.introductionFee || 0), 0);
    const salesUsers = demoUsers.filter((user) => user.role === 'Sales');
    const salesLabels = new Map(salesUsers.map((user) => [user.id, user.username]));

    const bySalesStaff = salesUsers.map((salesUser) => {
        const staffTalents = talents.filter((talent) => talent.salesStaffId === salesUser.id);
        const staffRegistered = staffTalents.filter(hasRegisteredIntroductionFee);
        const staffTotalFee = staffRegistered.reduce((sum, talent) => sum + Number(talent.introductionFee || 0), 0);
        return {
            salesStaffId: salesUser.id,
            salesStaff: salesUser.username,
            talentCount: staffTalents.length,
            totalIntroductionFee: staffTotalFee,
            averageIntroductionFee: staffRegistered.length ? Math.round(staffTotalFee / staffRegistered.length) : 0,
            unregisteredFeeCount: staffTalents.filter((talent) => !hasRegisteredIntroductionFee(talent)).length,
        };
    });

    const unassignedTalents = talents.filter((talent) => !salesLabels.has(talent.salesStaffId));
    if (unassignedTalents.length > 0) {
        const unassignedRegistered = unassignedTalents.filter(hasRegisteredIntroductionFee);
        const unassignedTotalFee = unassignedRegistered.reduce((sum, talent) => sum + Number(talent.introductionFee || 0), 0);
        bySalesStaff.push({
            salesStaffId: null,
            salesStaff: '未割当',
            talentCount: unassignedTalents.length,
            totalIntroductionFee: unassignedTotalFee,
            averageIntroductionFee: unassignedRegistered.length ? Math.round(unassignedTotalFee / unassignedRegistered.length) : 0,
            unregisteredFeeCount: unassignedTalents.filter((talent) => !hasRegisteredIntroductionFee(talent)).length,
        });
    }

    return {
        totalCount: talents.length,
        totalIntroductionFee,
        averageIntroductionFee: registeredTalents.length ? Math.round(totalIntroductionFee / registeredTalents.length) : 0,
        unregisteredFeeCount: talents.filter((talent) => !hasRegisteredIntroductionFee(talent)).length,
        bySalesStaff: bySalesStaff
            .filter((item) => item.talentCount > 0)
            .sort((a, b) => b.totalIntroductionFee - a.totalIntroductionFee || b.talentCount - a.talentCount || a.salesStaff.localeCompare(b.salesStaff, 'ja')),
    };
};

const createFakeJwt = (user) => {
    const header = window.btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
    const payload = window.btoa(JSON.stringify({
        nameid: user.id,
        unique_name: user.username,
        role: user.role,
        exp: Math.floor(Date.now() / 1000) + 60 * 60 * 24,
    }));

    return `${header}.${payload}.demo`;
};

const parseUserFromToken = (token) => {
    if (!token) return null;

    try {
        const payload = JSON.parse(window.atob(token.split('.')[1]));
        return {
            id: payload.nameid,
            username: payload.unique_name,
            role: payload.role,
        };
    } catch {
        return null;
    }
};

const TokenManager = {
    getAccessToken: () => localStorage.getItem(TOKEN_STORAGE_KEY) || localStorage.getItem('accessToken'),
    getRefreshToken: () => localStorage.getItem(REFRESH_STORAGE_KEY) || localStorage.getItem('refreshToken'),
    setTokens: (accessToken, refreshToken) => {
        localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken || 'demo-refresh-token');
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken || 'demo-refresh-token');
    },
    clearTokens: () => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_STORAGE_KEY);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('authToken');
    },
    isAuthenticated: () => Boolean(TokenManager.getAccessToken()),
};

const getCurrentUser = () => {
    const tokenUser = parseUserFromToken(TokenManager.getAccessToken());
    if (tokenUser) return tokenUser;
    return demoUsers[0];
};

const statusList = ['在職', '在職', '内定', '申請中', '待入国', '離職'];
const nationalities = ['ベトナム', '中国', 'ミャンマー', 'インドネシア', 'フィリピン', '韓国'];
const fields = ['介護', '外食', '飲食料品製造', '溶接', '建設', 'ビルメンテナンス'];
const prefectureAddresses = [
    '東京都新宿区西新宿2-8-1',
    '大阪府大阪市北区梅田3-1-1',
    '愛知県名古屋市中区栄3-1-1',
    '福岡県福岡市博多区博多駅前2-1-1',
    '北海道札幌市中央区北1条西2-1',
    '神奈川県横浜市西区みなとみらい2-2-1',
    '埼玉県さいたま市大宮区桜木町1-1',
    '千葉県千葉市中央区中央1-1-1',
];

const clientTemplates = [
    {
        id: 'demo-client-sakura',
        name: '桜ケアサービス株式会社',
        phone: '03-0000-1001',
        email: 'hr@sakura-care.example',
        contact: '佐藤 美咲',
        workplaces: [
            { id: 'demo-workplace-sakura-tokyo', name: '桜ケア 新宿事業所', address: '東京都新宿区西新宿2-8-1', phone: '03-0000-1101', email: 'shinjuku@sakura-care.example', contact: '田中 健', contactTitle: '施設長' },
            { id: 'demo-workplace-sakura-yokohama', name: '桜ケア 横浜事業所', address: '神奈川県横浜市西区みなとみらい2-2-1', phone: '045-000-1102', email: 'yokohama@sakura-care.example', contact: '山本 彩', contactTitle: '採用担当' },
        ],
    },
    {
        id: 'demo-client-minato',
        name: 'みなと食品製造株式会社',
        phone: '06-0000-2001',
        email: 'people@minato-food.example',
        contact: '中村 亮',
        workplaces: [
            { id: 'demo-workplace-minato-osaka', name: '大阪第一工場', address: '大阪府大阪市北区梅田3-1-1', phone: '06-0000-2101', email: 'osaka@minato-food.example', contact: '小林 結衣', contactTitle: '工場長' },
            { id: 'demo-workplace-minato-aichi', name: '名古屋加工センター', address: '愛知県名古屋市中区栄3-1-1', phone: '052-000-2102', email: 'nagoya@minato-food.example', contact: '伊藤 翔', contactTitle: '総務' },
        ],
    },
    {
        id: 'demo-client-hikari',
        name: 'ひかり外食グループ',
        phone: '092-000-3001',
        email: 'recruit@hikari-dining.example',
        contact: '松本 直子',
        workplaces: [
            { id: 'demo-workplace-hikari-fukuoka', name: '福岡セントラルキッチン', address: '福岡県福岡市博多区博多駅前2-1-1', phone: '092-000-3101', email: 'fukuoka@hikari-dining.example', contact: '井上 拓也', contactTitle: '店長' },
            { id: 'demo-workplace-hikari-sapporo', name: '札幌店舗統括', address: '北海道札幌市中央区北1条西2-1', phone: '011-000-3102', email: 'sapporo@hikari-dining.example', contact: '加藤 葵', contactTitle: '統括マネージャー' },
        ],
    },
];

const demoNames = [
    ['Nguyen Van An', 'グエン ヴァン アン'],
    ['Tran Thi Mai', 'チャン ティ マイ'],
    ['Li Wei', 'リ ウェイ'],
    ['Zhang Mei', 'チョウ メイ'],
    ['Aung Min', 'アウン ミン'],
    ['Thandar Hlaing', 'タンダー ライン'],
    ['Siti Rahma', 'シティ ラフマ'],
    ['Rizky Pratama', 'リズキ プラタマ'],
    ['Maria Santos', 'マリア サントス'],
    ['Kim Minjun', 'キム ミンジュン'],
    ['Pham Duc Anh', 'ファム ドゥック アン'],
    ['Chen Yu', 'チェン ユー'],
];

const createClientOptions = () => clientTemplates.map((organization) => ({
    ...organization,
    normalizedName: organization.name,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    workplaces: organization.workplaces.map((workplace) => ({
        ...workplace,
        organizationId: organization.id,
        normalizedName: workplace.name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
    })),
}));

const makeLoan = (talentId, index, loanDate, totalAmount) => {
    const repaid = Math.floor(totalAmount * (index % 4 === 0 ? 1 : 0.38));
    const remainingBalance = Math.max(0, totalAmount - repaid);
    const loanEntryId = `demo-loan-entry-${index}`;
    const repaymentId = `demo-repayment-${index}`;
    const category = index % 3 === 0 ? LOAN_ENTRY_CATEGORY.RENTAL : LOAN_ENTRY_CATEGORY.LIVING_EXPENSE;
    const subjectType = index % 5 === 0 ? LOAN_SUBJECT_TYPE.CORPORATE : LOAN_SUBJECT_TYPE.INDIVIDUAL;
    const repaymentCategory = category === LOAN_ENTRY_CATEGORY.RENTAL
        ? REPAYMENT_CATEGORY.RENTAL
        : REPAYMENT_CATEGORY.ADVANCE;

    const loan = {
        id: `demo-loan-${index}`,
        talentId,
        accountingStaffId: 'demo-user-accounting',
        totalAmount,
        details: '生活立ち上げ費用・交通費の一時貸付',
        loanDate,
        repaymentStartDate: addMonths(loanDate, 1),
        repaymentEndDate: addMonths(loanDate, 10),
        remainingBalance,
        status: remainingBalance <= 0 ? 'Completed' : 'Active',
        completedAt: remainingBalance <= 0 ? addMonths(loanDate, 8) : null,
        loanEntries: [
            {
                id: loanEntryId,
                loanId: `demo-loan-${index}`,
                amount: totalAmount,
                category,
                subjectType,
                loanDate,
                plannedEndDate: addMonths(loanDate, 10),
                note: 'デモ用貸付明細',
                isDeleted: false,
            },
        ],
        repayments: repaid > 0
            ? [
                {
                    id: repaymentId,
                    loanId: `demo-loan-${index}`,
                    amount: repaid,
                    category: repaymentCategory,
                    subjectType,
                    loanEntryId: repaymentCategory === REPAYMENT_CATEGORY.RENTAL ? loanEntryId : null,
                    repaymentDate: addMonths(loanDate, 3),
                    createdAt: addMonths(loanDate, 3),
                },
            ]
            : [],
        createdAt: loanDate,
        updatedAt: addMonths(loanDate, 3),
    };
    recalculateDemoLoanAggregates(loan);
    loan.status = loan.remainingBalance <= 0 ? 'Completed' : 'Active';
    loan.completedAt = loan.remainingBalance <= 0 ? addMonths(loanDate, 8) : null;
    return loan;
};

const createInitialState = () => {
    const today = new Date();
    const users = clone(demoUsers);
    const clients = createClientOptions();
    const talents = [];
    const residences = [];
    const loans = [];
    const attachments = [];
    const contracts = [];
    const feeChanges = {};
    const billingOverrides = {};

    for (let index = 0; index < 36; index += 1) {
        const [fullName, fullNameFurigana] = demoNames[index % demoNames.length];
        const salesStaff = index % 3 === 0 ? users[3] : users[2];
        const client = clients[index % clients.length];
        const workplace = client.workplaces[index % client.workplaces.length];
        const status = statusList[index % statusList.length];
        const field = fields[index % fields.length];
        const nationality = nationalities[index % nationalities.length];
        const startDate = addMonths(today, -1 * (index + 3));
        const expiryOffset = [24, 42, 58, 92, 180, 365, -12][index % 7];
        const residenceCardExpiry = addDays(today, expiryOffset);
        const managementFee = index % 8 === 0 ? 0 : 25000 + (index % 5) * 3000;
        const initialCost = index % 6 === 0 ? null : 120000 + (index % 4) * 30000;
        const talentId = `demo-talent-${String(index + 1).padStart(3, '0')}`;
        const residenceId = `demo-residence-${String(index + 1).padStart(3, '0')}`;
        const hasLoan = index % 3 !== 1;
        const loan = hasLoan ? makeLoan(talentId, index + 1, addMonths(today, -1 * (index + 2)), 90000 + (index % 5) * 40000) : null;
        const loanSummary = loan ? getLoanFinancialSummary(loan) : null;
        const activeLoanBorrowedAmount = loan ? loan.totalAmount : 0;
        const activeLoanRentalAmount = loan ? getActiveLoanEntries(loan).filter(isRentalLoanEntry).reduce((sum, entry) => sum + toNumber(entry.amount), 0) : 0;
        const activeLoanAdvanceAmount = loan ? getActiveLoanEntries(loan).filter((entry) => !isRentalLoanEntry(entry)).reduce((sum, entry) => sum + toNumber(entry.amount), 0) : 0;
        const activeLoanRentalRepaidAmount = loanSummary ? loanSummary.rentalRepaid : 0;
        const activeLoanAdvanceRepaidAmount = loanSummary ? loanSummary.advanceRepaid : 0;
        const activeLoanRepaidAmount = loanSummary ? loanSummary.totalRepaid : 0;
        const activeLoanRemainingAmount = loanSummary ? loanSummary.totalRemaining : 0;
        const isPaused = index % 11 === 0 && status === '在職';
        const isSpecifiedSkill2Seed = index % 10 === 2 || index % 10 === 7;
        const seededCurrentResidenceStatus = isSpecifiedSkill2Seed ? '特定技能2号' : '特定技能1号';
        const seededApplicationType = isSpecifiedSkill2Seed ? '資格変更' : '期間更新';
        const introductionFee = isSpecifiedSkill2Seed
            ? (index % 20 === 7 ? 0 : 180000 + (index % 4) * 50000)
            : null;
        const renewalEntries = isSpecifiedSkill2Seed
            ? [
                {
                    id: `demo-renewal-old-${index}`,
                    renewalDate: addMonths(today, -18),
                    renewalGrantDate: addMonths(today, -17),
                    renewalNumber: `UP-${String(index + 1).padStart(3, '0')}-1`,
                    residenceStatus: '特定技能1号',
                    renewalApplicationType: '期間更新',
                    submissionMethod: 'オンライン提出',
                    submissionAgent: 'スティーブン',
                    createdAt: addMonths(today, -18),
                },
                {
                    id: `demo-renewal-current-${index}`,
                    renewalDate: addDays(today, -20),
                    renewalGrantDate: addDays(today, -5),
                    renewalNumber: `UP-${String(index + 1).padStart(3, '0')}-2`,
                    residenceStatus: '特定技能2号',
                    renewalApplicationType: '資格変更',
                    submissionMethod: 'オンライン提出',
                    submissionAgent: 'スティーブン',
                    createdAt: addDays(today, -20),
                },
            ]
            : (expiryOffset < 120 ? [
                {
                    id: `demo-renewal-${index}`,
                    renewalDate: addDays(today, -20),
                    renewalGrantDate: null,
                    renewalNumber: `UP-${String(index + 1).padStart(3, '0')}`,
                    residenceStatus: '特定技能1号',
                    renewalApplicationType: '期間更新',
                    submissionMethod: 'オンライン提出',
                    submissionAgent: 'スティーブン',
                    createdAt: addDays(today, -20),
                },
            ] : []);

        const residencePermit = {
            id: residenceId,
            talentId,
            residenceStaffId: 'demo-user-residence',
            applicationDate: addMonths(startDate, -2),
            grantDate: addMonths(startDate, -1),
            applicationMethod: index % 2 === 0 ? 'オンライン提出' : '対面提出',
            applicationNumber: `R-${new Date().getFullYear()}-${String(index + 101).padStart(4, '0')}`,
            residenceStatus: seededCurrentResidenceStatus,
            applicationType: seededApplicationType,
            applicant: '行政書士法人デモサポート',
            residenceCardNumber: `AB${String(10000000 + index * 1731)}`,
            residenceCardExpiry,
            passportNumber: `P${String(7000000 + index * 991)}`,
            passportExpiry: addMonths(today, 18 + (index % 12)),
            status: status === '離職' ? '退職済み' : '在職中',
            interviewDate: addMonths(startDate, -1),
            startDate,
            endDate: status === '離職' ? addMonths(startDate, 8) : null,
            renewalDate: expiryOffset < 90 ? addDays(today, -10) : null,
            renewalGrantDate: null,
            updatedAt: addDays(today, -1 * (index % 14)),
            renewalEntries,
        };

        const talent = {
            id: talentId,
            salesStaffId: salesStaff.id,
            salesStaff,
            managementNumber: `TM-${String(index + 1).padStart(4, '0')}`,
            fullName,
            fullNameFurigana,
            gender: index % 2 === 0 ? '男' : '女',
            dateOfBirth: `${1988 + (index % 12)}-${String((index % 12) + 1).padStart(2, '0')}-${String((index % 20) + 5).padStart(2, '0')}`,
            nationality,
            field,
            address: prefectureAddresses[(index + 2) % prefectureAddresses.length],
            phoneNumber: `080-${String(1000 + index).padStart(4, '0')}-${String(3000 + index * 7).padStart(4, '0')}`,
            affiliatedOrganizationName: client.name,
            affiliatedOrganizationPhone: client.phone,
            affiliatedOrganizationEmail: client.email,
            affiliatedOrganizationContact: client.contact,
            clientOrganizationId: client.id,
            workplaceName: workplace.name,
            workplaceAddress: workplace.address,
            workplacePhone: workplace.phone,
            workplaceEmail: workplace.email,
            workplaceContact: workplace.contact,
            workplaceContactTitle: workplace.contactTitle,
            clientWorkplaceId: workplace.id,
            initialCost,
            initialCostNotCharged: index % 13 === 0,
            introductionFee,
            managementFee,
            managementFeeNotCharged: index % 8 === 0,
            firstMonthManagementFee: index % 4 === 0 ? Math.floor(managementFee / 2) : managementFee,
            billingPlanType: index % 5 === 0 ? '段階管理費' : '通常',
            includedManagementMonths: index % 5 === 0 ? 2 : null,
            tier1Months: index % 5 === 0 ? 6 : null,
            tier1ManagementFee: index % 5 === 0 ? 18000 : null,
            tier2Months: index % 5 === 0 ? 12 : null,
            tier2ManagementFee: index % 5 === 0 ? 24000 : null,
            contractEndDate: addMonths(startDate, 36),
            employmentPauseType: isPaused ? '一時帰国' : 'なし',
            employmentPauseStartDate: isPaused ? addDays(today, -14) : null,
            employmentPauseEndDate: isPaused ? addDays(today, 21) : null,
            employmentStatus: status,
            createdAt: addMonths(today, -1 * (index + 4)),
            updatedAt: addDays(today, -1 * (index % 10)),
            residencePermit,
            activeLoanBorrowedAmount,
            activeLoanRentalAmount,
            activeLoanAdvanceAmount,
            activeLoanRentalRepaidAmount,
            activeLoanAdvanceRepaidAmount,
            activeLoanRepaidAmount,
            activeLoanRemainingAmount,
            loans: loan ? [loan] : [],
        };

        talents.push(talent);
        residences.push(residencePermit);
        if (loan) loans.push(loan);

        attachments.push({
            id: `demo-attachment-photo-${index}`,
            talentId,
            fileName: `${talent.managementNumber}_photo.svg`,
            filePath: `demo://attachments/${talentId}/photo.svg`,
            fileType: 'image/svg+xml',
            fileSize: 2048,
            sourceModule: 'Photo',
            createdAt: talent.createdAt,
        });

        if (loan && index % 6 === 0) {
            attachments.push({
                id: `demo-attachment-loan-${index}`,
                talentId,
                fileName: `${talent.managementNumber}_loan-detail.pdf`,
                filePath: `demo://attachments/${talentId}/loan-detail.pdf`,
                fileType: 'application/pdf',
                fileSize: 148000,
                sourceModule: 'Loan',
                loanEntryId: loan.loanEntries[0]?.id || null,
                createdAt: addDays(today, -2 - (index % 5)),
            });
        }

        if (index % 2 === 0) {
            attachments.push({
                id: `demo-attachment-residence-${index}`,
                talentId,
                fileName: `${talent.managementNumber}_residence-card.pdf`,
                filePath: `demo://attachments/${talentId}/residence-card.pdf`,
                fileType: 'application/pdf',
                fileSize: 145000,
                sourceModule: 'Residence',
                createdAt: addDays(today, -1 * (index % 9)),
            });
        }

        feeChanges[talentId] = index % 4 === 0 ? [
            {
                id: `demo-fee-change-${index}`,
                talentId,
                effectiveMonth: addMonths(today, -2).slice(0, 7),
                oldFee: managementFee - 2000,
                newFee: managementFee,
                note: '契約更新に伴う管理費調整',
                createdAt: addMonths(today, -2),
            },
        ] : [];

        billingOverrides[talentId] = {};
    }

    contracts.push(
        { id: 'demo-contract-org-1', ownerType: 'organization', ownerId: clients[0].id, fileName: '基本契約書_桜ケア.pdf', fileType: 'application/pdf', createdAt: addMonths(today, -3) },
        { id: 'demo-contract-org-2', ownerType: 'organization', ownerId: clients[1].id, fileName: '人材紹介契約_みなと食品.pdf', fileType: 'application/pdf', createdAt: addMonths(today, -2) },
        { id: 'demo-contract-workplace-1', ownerType: 'workplace', ownerId: clients[0].workplaces[0].id, fileName: '新宿事業所_受入条件書.pdf', fileType: 'application/pdf', createdAt: addMonths(today, -1) },
    );

    return {
        demoDataVersion: DEMO_DATA_VERSION,
        users,
        clients,
        talents,
        residences,
        loans,
        loanEntryCategories: createDefaultLoanEntryCategories(),
        attachments,
        contracts,
        feeChanges,
        billingOverrides,
        notifications: [
            {
                id: 'demo-notification-1',
                title: '在留カード期限確認',
                content: '30日以内に期限を迎える人材がいます。リスト画面の在留カード期限フィルターで確認してください。',
                notificationType: 'Reminder',
                status: 'Sent',
                createdAt: addDays(today, -2),
                sentTime: addDays(today, -1),
                createdBy: 'demo-user-admin',
                createdByName: 'demo_manager',
                targetUserIds: ['demo-user-super-admin', 'demo-user-sales-a', 'demo-user-residence'],
                isRead: false,
                readAt: null,
            },
            {
                id: 'demo-notification-2',
                title: 'デモ環境のお知らせ',
                content: 'この環境は静的デモです。入力内容はブラウザ内にのみ保存され、正式データには影響しません。',
                notificationType: 'Update',
                status: 'Sent',
                createdAt: addDays(today, -5),
                sentTime: addDays(today, -5),
                createdBy: 'demo-user-admin',
                createdByName: 'demo_manager',
                targetUserIds: ['demo-user-super-admin', 'demo-user-sales-a', 'demo-user-residence', 'demo-user-accounting'],
                isRead: true,
                readAt: addDays(today, -4),
            },
        ],
        news: [
            {
                id: 'demo-news-1',
                title: '特定技能制度の運用見直しポイント',
                url: 'https://example.com/demo-news-1',
                sourceName: 'Demo Immigration Brief',
                publishedAt: addDays(today, -3),
                summaryJa: '受入れ機関の支援体制と定期報告の確認事項が更新されました。',
                summaryZh: '受入机构支援体制和定期报告确认事项有更新。',
                category: '制度改正',
                importance: 'high',
                status: 'Published',
                isRead: false,
                readAt: null,
                createdAt: addDays(today, -3),
            },
            {
                id: 'demo-news-2',
                title: '介護分野の試験日程が公開',
                url: 'https://example.com/demo-news-2',
                sourceName: 'Demo Skills News',
                publishedAt: addDays(today, -8),
                summaryJa: '介護分野の国内試験日程が追加されました。',
                summaryZh: '护理领域国内考试日程已追加。',
                category: '試験',
                importance: 'normal',
                status: 'Published',
                isRead: true,
                readAt: addDays(today, -7),
                createdAt: addDays(today, -8),
            },
        ],
    };
};

const ensureDemoDataVersion = (state) => {
    if (!state || Number(state.demoDataVersion || 0) >= DEMO_DATA_VERSION) {
        return { state, changed: false };
    }

    const today = new Date();
    const talentIndexById = new Map((state.talents || []).map((talent, index) => [talent.id, index]));
    const talentById = new Map((state.talents || []).map((talent) => [talent.id, talent]));
    const residencesByTalentId = new Map((state.residences || []).map((residence) => [residence.talentId, residence]));

    (state.talents || []).forEach((talent, index) => {
        const storedResidence = residencesByTalentId.get(talent.id);
        const residencePermit = talent.residencePermit || storedResidence;
        if (!residencePermit) {
            talent.introductionFee = talent.introductionFee ?? null;
            return;
        }

        residencePermit.residenceStatus = residencePermit.residenceStatus || '特定技能1号';
        residencePermit.applicationType = residencePermit.applicationType || '期間更新';
        residencePermit.renewalEntries = Array.isArray(residencePermit.renewalEntries) ? residencePermit.renewalEntries : [];

        const isSpecifiedSkill2Seed = index % 10 === 2 || index % 10 === 7;
        if (isSpecifiedSkill2Seed) {
            residencePermit.residenceStatus = '特定技能2号';
            residencePermit.applicationType = '資格変更';
            talent.introductionFee = talent.introductionFee ?? (index % 20 === 7 ? 0 : 180000 + (index % 4) * 50000);

            if (!residencePermit.renewalEntries.some((entry) => isSpecifiedSkill2Status(entry.residenceStatus))) {
                residencePermit.renewalEntries.push({
                    id: `demo-renewal-current-${index}`,
                    renewalDate: addDays(today, -20),
                    renewalGrantDate: addDays(today, -5),
                    renewalNumber: `UP-${String(index + 1).padStart(3, '0')}-2`,
                    residenceStatus: '特定技能2号',
                    renewalApplicationType: '資格変更',
                    submissionMethod: 'オンライン提出',
                    submissionAgent: 'スティーブン',
                    createdAt: addDays(today, -20),
                });
            }
        } else {
            talent.introductionFee = talent.introductionFee ?? null;
        }

        talent.residencePermit = residencePermit;
        talent.currentResidenceStatus = getCurrentResidenceStatus(residencePermit);
        talent.isSpecifiedSkill2 = isSpecifiedSkill2Status(talent.currentResidenceStatus);
        if (storedResidence && storedResidence !== residencePermit) {
            Object.assign(storedResidence, residencePermit);
        }
    });

    const existingCategories = Array.isArray(state.loanEntryCategories)
        ? state.loanEntryCategories
        : [];
    const categoryMap = new Map(
        existingCategories
            .filter(category => category?.name)
            .map(category => [normalizeCategoryNameForStorage(category.name), category])
    );

    createDefaultLoanEntryCategories().forEach((category) => {
        if (!categoryMap.has(category.normalizedName)) {
            categoryMap.set(category.normalizedName, category);
        }
    });

    (state.loans || []).flatMap(getActiveLoanEntries).forEach((entry) => {
        const name = normalizeLoanEntryCategory(entry?.category);
        const normalizedName = normalizeCategoryNameForStorage(name);
        if (!name || name === LOAN_ENTRY_CATEGORY.RENTAL || categoryMap.has(normalizedName)) {
            return;
        }

        categoryMap.set(normalizedName, {
            id: createId('demo-loan-entry-category'),
            name,
            normalizedName,
            isDefault: false,
            isActive: true,
            sortOrder: 100 + categoryMap.size,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });
    });

    state.loanEntryCategories = Array.from(categoryMap.values())
        .filter(category => category.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100));

    (state.loans || []).forEach((loan) => {
        const activeEntries = getActiveLoanEntries(loan);
        activeEntries.forEach((entry) => {
            entry.subjectType = normalizeLoanSubjectType(entry.subjectType);
        });

        (loan.repayments || []).forEach((repayment) => {
            const linkedEntry = activeEntries.find((entry) => entry.id === repayment.loanEntryId);
            const rentalEntry = linkedEntry && isRentalLoanEntry(linkedEntry)
                ? linkedEntry
                : activeEntries.find(isRentalLoanEntry) || null;

            if ((repayment.category || repayment.Category) === REPAYMENT_CATEGORY.RENTAL && rentalEntry) {
                repayment.category = REPAYMENT_CATEGORY.RENTAL;
                repayment.loanEntryId = rentalEntry.id;
                repayment.amount = toNumber(rentalEntry.amount);
                repayment.subjectType = normalizeLoanSubjectType(repayment.subjectType || getLoanEntrySubjectType(rentalEntry));
                return;
            }

            const inferredCategory = rentalEntry && activeEntries.length === 1 && isRentalLoanEntry(rentalEntry)
                ? REPAYMENT_CATEGORY.RENTAL
                : REPAYMENT_CATEGORY.ADVANCE;

            if (inferredCategory === REPAYMENT_CATEGORY.RENTAL && rentalEntry) {
                repayment.category = REPAYMENT_CATEGORY.RENTAL;
                repayment.loanEntryId = rentalEntry.id;
                repayment.amount = toNumber(rentalEntry.amount);
                repayment.subjectType = normalizeLoanSubjectType(repayment.subjectType || getLoanEntrySubjectType(rentalEntry));
                return;
            }

            repayment.category = REPAYMENT_CATEGORY.ADVANCE;
            repayment.loanEntryId = null;
            repayment.subjectType = normalizeLoanSubjectType(repayment.subjectType);
        });

        recalculateDemoLoanAggregates(loan);
    });

    const existingAttachmentIds = new Set((state.attachments || []).map((attachment) => attachment.id));
    (state.loans || []).forEach((loan) => {
        const talentIndex = talentIndexById.get(loan.talentId);
        if (!Number.isInteger(talentIndex) || talentIndex % 6 !== 0) {
            return;
        }

        const attachmentId = `demo-attachment-loan-${talentIndex}`;
        if (existingAttachmentIds.has(attachmentId)) {
            return;
        }

        const talent = talentById.get(loan.talentId);
        const loanEntryId = getActiveLoanEntries(loan)[0]?.id || null;
        if (!talent || !loanEntryId) {
            return;
        }

        state.attachments = Array.isArray(state.attachments) ? state.attachments : [];
        state.attachments.push({
            id: attachmentId,
            talentId: loan.talentId,
            fileName: `${talent.managementNumber || talent.fullName || 'demo'}_loan-detail.pdf`,
            filePath: `demo://attachments/${loan.talentId}/loan-detail.pdf`,
            fileType: 'application/pdf',
            fileSize: 148000,
            sourceModule: 'Loan',
            loanEntryId,
            createdAt: addDays(today, -2 - (talentIndex % 5)),
        });
        existingAttachmentIds.add(attachmentId);
    });

    state.demoDataVersion = DEMO_DATA_VERSION;
    return { state, changed: true };
};

const SHARED_STATE_INIT_TIMEOUT_MS = 2500;
const SHARED_STATE_BACKGROUND_TIMEOUT_MS = 15000;

let sharedStateInitialized = false;
let sharedStateSyncPromise = null;
let sharedStateBackgroundPromise = null;
let localStateRevision = 0;
let pendingSharedSave = Promise.resolve();

const writeLocalState = (state) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    localStateRevision += 1;
};

const isUsableDemoState = (state) => (
    state
    && Array.isArray(state.users)
    && Array.isArray(state.talents)
    && Array.isArray(state.clients)
);

const loadState = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const migrated = ensureDemoDataVersion(JSON.parse(raw));
            if (migrated.changed) {
                writeLocalState(migrated.state);
            }
            return migrated.state;
        }
    } catch {
        // fall through to seed
    }

    const initialState = createInitialState();
    writeLocalState(initialState);
    return initialState;
};

const saveState = (state) => {
    writeLocalState(state);

    if (isSharedDemoMode) {
        const snapshot = clone(state);
        pendingSharedSave = pendingSharedSave
            .catch(() => null)
            .then(() => saveSharedDemoState(snapshot))
            .catch((error) => {
                console.warn('Shared demo state save failed:', error);
            });
    }
};

const syncSharedStateFromRemote = async ({ timeoutMs, trackInFlight = true } = {}) => {
    if (trackInFlight && sharedStateSyncPromise) {
        return sharedStateSyncPromise;
    }

    const startedRevision = localStateRevision;
    const syncPromise = (async () => {
        const remoteState = await fetchSharedDemoState({ timeoutMs });

        if (isUsableDemoState(remoteState)) {
            const migrated = ensureDemoDataVersion(remoteState);
            if (localStateRevision === startedRevision) {
                writeLocalState(migrated.state);
            }
            if (migrated.changed) {
                await saveSharedDemoState(migrated.state);
            }
            sharedStateInitialized = true;
            return localStateRevision === startedRevision ? migrated.state : loadState();
        }

        const localState = loadState();
        const initialState = isUsableDemoState(localState) ? localState : createInitialState();
        writeLocalState(initialState);
        await saveSharedDemoState(initialState);
        sharedStateInitialized = true;
        return initialState;
    })();

    if (trackInFlight) {
        sharedStateSyncPromise = syncPromise.finally(() => {
            sharedStateSyncPromise = null;
        });
        return sharedStateSyncPromise;
    }

    return syncPromise;
};

const scheduleSharedStateBackgroundSync = () => {
    if (!isSharedDemoMode || sharedStateInitialized || sharedStateBackgroundPromise) {
        return;
    }

    sharedStateBackgroundPromise = syncSharedStateFromRemote({
        timeoutMs: SHARED_STATE_BACKGROUND_TIMEOUT_MS,
        trackInFlight: false,
    })
        .catch((error) => {
            console.warn('Shared demo state background sync failed:', error);
        })
        .finally(() => {
            sharedStateBackgroundPromise = null;
        });
};

const initializeSharedState = async ({ timeoutMs = SHARED_STATE_INIT_TIMEOUT_MS, retryInBackground = true } = {}) => {
    if (!isSharedDemoMode || sharedStateInitialized) {
        return loadState();
    }

    try {
        return await syncSharedStateFromRemote({ timeoutMs });
    } catch (error) {
        console.warn('Shared demo state initialization failed, using local state:', error);
        if (retryInBackground) {
            scheduleSharedStateBackgroundSync();
        }
        return loadState();
    }
};

const updateState = (updater) => {
    const state = loadState();
    const nextState = updater(state) || state;
    saveState(nextState);
    return nextState;
};

const getVisibleTalents = (state) => {
    const user = getCurrentUser();
    if (user.role === 'Sales') {
        return state.talents.filter((talent) => talent.salesStaffId === user.id);
    }
    return state.talents;
};

const applyTalentFilters = (talents, params = {}) => {
    let result = [...talents];

    if (params.searchTerm) {
        const term = String(params.searchTerm).toLowerCase();
        result = result.filter((talent) => [
            talent.fullName,
            talent.fullNameFurigana,
            talent.managementNumber,
            talent.affiliatedOrganizationName,
            talent.workplaceName,
            talent.nationality,
            talent.field,
        ].some((value) => String(value || '').toLowerCase().includes(term)));
    }

    if (params.salesStaffId) {
        result = result.filter((talent) => talent.salesStaffId === params.salesStaffId);
    }

    if (Array.isArray(params.nationalities) && params.nationalities.length > 0) {
        result = result.filter((talent) => params.nationalities.includes(talent.nationality));
    }

    if (Array.isArray(params.workFields) && params.workFields.length > 0) {
        result = result.filter((talent) => params.workFields.includes(talent.field));
    }

    if (params.employmentStatus && params.employmentStatus !== 'すべて') {
        result = result.filter((talent) => talent.employmentStatus === params.employmentStatus);
    }

    if (params.initialCostMin) {
        result = result.filter((talent) => Number(talent.initialCost || 0) >= Number(params.initialCostMin));
    }

    if (params.managementFeeMin) {
        result = result.filter((talent) => Number(talent.managementFee || 0) >= Number(params.managementFeeMin));
    }

    if (params.noPagination && params.initialCostStartMonth) {
        result = result.filter((talent) => talent.createdAt.slice(0, 7) >= params.initialCostStartMonth);
    }

    return result;
};

const sortTalents = (talents, params = {}) => {
    const sortBy = params.sortBy || 'createdAt';
    const direction = params.descending === false ? 1 : -1;

    return [...talents].sort((a, b) => {
        const left = a[sortBy] ?? '';
        const right = b[sortBy] ?? '';
        if (left > right) return direction;
        if (left < right) return -direction;
        return 0;
    });
};

const paginate = (items, params = {}) => {
    const pageSize = Number(params.pageSize || 50);
    const currentPageValue = Number(params.page || 1);
    const totalCount = items.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const currentPageNumber = Math.min(Math.max(1, currentPageValue), totalPages);
    const start = (currentPageNumber - 1) * pageSize;

    return {
        data: items.slice(start, start + pageSize),
        currentPage: currentPageNumber,
        totalPages,
        totalCount,
        pageSize,
    };
};

const calculateFeeTotals = (talents) => talents.reduce((totals, talent) => {
    const toAmount = (value) => {
        const amount = Number(value);
        return Number.isFinite(amount) ? amount : 0;
    };

    totals.initialCost += talent.initialCostNotCharged ? 0 : toAmount(talent.initialCost);
    totals.managementFee += talent.managementFeeNotCharged ? 0 : toAmount(talent.managementFee);
    return totals;
}, { initialCost: 0, managementFee: 0 });

const distribution = (items, resolver, key = 'label') => {
    const counts = new Map();
    items.forEach((item) => {
        const label = resolver(item) || '未設定';
        counts.set(label, (counts.get(label) || 0) + 1);
    });

    const total = items.length || 1;
    return Array.from(counts.entries()).map(([label, count]) => ({
        [key]: label,
        label,
        count,
        percentage: Math.round((count / total) * 1000) / 10,
    }));
};

const monthsBack = (count = 6) => {
    const date = new Date();
    return Array.from({ length: count }).map((_, index) => {
        const next = new Date(date);
        next.setMonth(date.getMonth() - (count - index - 1));
        return next.toISOString().slice(0, 7);
    });
};

const calcManagementView = (talents) => {
    const billable = talents.filter((talent) => !talent.managementFeeNotCharged && Number(talent.managementFee || 0) > 0);
    const totalFee = billable.reduce((sum, talent) => sum + Number(talent.managementFee || 0), 0);
    const noChargeTalents = talents.filter((talent) => talent.managementFeeNotCharged).length;

    return {
        totalFee,
        talentsWithFee: billable.length,
        targetTalentsCount: talents.length,
        activeTalentsCount: talents.filter((talent) => talent.employmentStatus === '在職').length,
        noChargeTalents,
        averageFeePerTalent: billable.length > 0 ? Math.round(totalFee / billable.length) : 0,
        periodStart: `${currentMonth()}-01`,
        periodEnd: `${currentMonth()}-28`,
    };
};

const calcManagementFeeStats = (talents) => {
    const current = calcManagementView(talents);
    const rangeActual = { ...current, totalFee: current.totalFee * 3, periodStart: monthsBack(3)[0], periodEnd: `${currentMonth()}-28` };
    const rangeForecast = { ...current, totalFee: current.totalFee * 6, periodStart: currentMonth(), periodEnd: monthsBack(1)[0] };
    const rangeCombined = { ...current, totalFee: current.totalFee * 9, periodStart: monthsBack(3)[0], periodEnd: monthsBack(6)[5] };

    const bySalesStaff = demoUsers
        .filter((user) => user.role === 'Sales')
        .map((salesUser) => {
            const staffTalents = talents.filter((talent) => talent.salesStaffId === salesUser.id);
            const staffCurrent = calcManagementView(staffTalents);
            return {
                salesStaffId: salesUser.id,
                salesStaff: salesUser.username,
                talentCount: staffTalents.length,
                activeTalentCount: staffTalents.filter((talent) => talent.employmentStatus === '在職').length,
                totalMonthlyFee: staffCurrent.totalFee,
                noChargeTalents: staffCurrent.noChargeTalents,
                views: {
                    current: staffCurrent,
                    rangeActual: { ...staffCurrent, totalFee: staffCurrent.totalFee * 3 },
                    rangeForecast: { ...staffCurrent, totalFee: staffCurrent.totalFee * 6 },
                    rangeCombined: { ...staffCurrent, totalFee: staffCurrent.totalFee * 9 },
                },
            };
        });

    return {
        totalMonthlyFee: current.totalFee,
        averageFeePerTalent: current.averageFeePerTalent,
        medianFee: 28000,
        maxFee: 37000,
        minFee: 18000,
        projectedAnnualRevenue: current.totalFee * 12,
        firstMonthFeeTotal: talents.reduce((sum, talent) => sum + Number(talent.firstMonthManagementFee || 0), 0),
        firstMonthFeeCount: talents.filter((talent) => Number(talent.firstMonthManagementFee || 0) > 0).length,
        talentsWithFee: current.talentsWithFee,
        noChargeTalents: current.noChargeTalents,
        activeTalentsCount: current.activeTalentsCount,
        currentMonth: current,
        views: {
            current,
            rangeActual,
            rangeForecast,
            rangeCombined,
        },
        bySalesStaff,
    };
};

const calcInitialCostStats = (talents) => {
    const chargeable = talents.filter((talent) => !talent.initialCostNotCharged && Number(talent.initialCost || 0) > 0);
    const totalInitialCost = chargeable.reduce((sum, talent) => sum + Number(talent.initialCost || 0), 0);
    const stats = {
        totalInitialCost,
        averageInitialCost: chargeable.length ? Math.round(totalInitialCost / chargeable.length) : 0,
        medianInitialCost: 150000,
        maxCost: 210000,
        minCost: 120000,
        talentsWithCost: chargeable.length,
        coverageRate: talents.length ? Math.round((chargeable.length / talents.length) * 1000) / 10 : 0,
        noChargeTalents: talents.filter((talent) => talent.initialCostNotCharged).length,
        chargeablePopulation: talents.length,
    };

    return {
        ...stats,
        bySalesStaff: demoUsers
            .filter((user) => user.role === 'Sales')
            .map((salesUser) => {
                const staffTalents = talents.filter((talent) => talent.salesStaffId === salesUser.id);
                const staffStats = calcInitialCostStatsFlat(staffTalents);
                return {
                    salesStaffId: salesUser.id,
                    salesStaff: salesUser.username,
                    talentCount: staffTalents.length,
                    ...staffStats,
                };
            }),
    };
};

const calcInitialCostStatsFlat = (talents) => {
    const chargeable = talents.filter((talent) => !talent.initialCostNotCharged && Number(talent.initialCost || 0) > 0);
    const totalInitialCost = chargeable.reduce((sum, talent) => sum + Number(talent.initialCost || 0), 0);
    return {
        totalInitialCost,
        averageInitialCost: chargeable.length ? Math.round(totalInitialCost / chargeable.length) : 0,
        medianInitialCost: 150000,
        maxCost: chargeable.reduce((max, talent) => Math.max(max, Number(talent.initialCost || 0)), 0),
        minCost: chargeable.reduce((min, talent) => Math.min(min, Number(talent.initialCost || 0)), chargeable[0]?.initialCost || 0),
        talentsWithCost: chargeable.length,
        coverageRate: talents.length ? Math.round((chargeable.length / talents.length) * 1000) / 10 : 0,
        noChargeTalents: talents.filter((talent) => talent.initialCostNotCharged).length,
        chargeablePopulation: talents.length,
    };
};

const buildCoreAnalytics = (sourceTalents) => {
    const allTalents = decorateTalents(sourceTalents);
    const specifiedSkill2Stats = buildSpecifiedSkill2Stats(allTalents);
    const talents = allTalents.filter((talent) => !isSpecifiedSkill2Talent(talent));
    const months = monthsBack(6);
    const salesUsers = demoUsers.filter((user) => user.role === 'Sales');
    const monthlyNewTalents = months.map((month, index) => ({
        month,
        count: Math.max(1, Math.round(talents.length / 10) + (index % 3)),
    }));
    const monthlyManagementFeeRevenue = months.map((month, index) => ({
        month,
        revenue: talents.reduce((sum, talent) => sum + Number(talent.managementFee || 0), 0) + index * 45000,
    }));

    return {
        totalCount: talents.length,
        employmentStatusDistribution: distribution(talents, (talent) => talent.employmentStatus, 'status'),
        nationalityDistribution: distribution(talents, (talent) => talent.nationality, 'nationality'),
        fieldDistribution: distribution(talents, (talent) => talent.field, 'field'),
        monthlyNewTalents,
        monthlyManagementFeeRevenue,
        quarterlyStats: ['Q1', 'Q2', 'Q3', 'Q4'].map((quarter, index) => ({
            quarter,
            count: Math.max(2, Math.round(talents.length / 4) + index),
        })),
        yearOverYearComparison: {
            quarters: ['Q1', 'Q2', 'Q3', 'Q4'],
            series: [
                { label: '2025', data: [6, 9, 11, 10] },
                { label: '2026', data: [8, 12, 14, 16] },
            ],
        },
        salesStaffMonthlyRevenue: {
            months,
            series: salesUsers.map((salesUser, index) => ({
                salesStaff: salesUser.username,
                data: months.map((_, monthIndex) => 180000 + index * 60000 + monthIndex * 20000),
            })),
        },
        salesStaffMonthlyTrend: {
            months,
            series: salesUsers.map((salesUser, index) => ({
                salesStaff: salesUser.username,
                counts: months.map((_, monthIndex) => 2 + index + (monthIndex % 3)),
                data: months.map((_, monthIndex) => 2 + index + (monthIndex % 3)),
            })),
        },
        managementFeeStats: calcManagementFeeStats(talents),
        initialCostStats: calcInitialCostStats(talents),
        specifiedSkill2Stats,
    };
};

const buildAdminAnalytics = (sourceTalents) => {
    const allTalents = decorateTalents(sourceTalents);
    const talents = allTalents.filter((talent) => !isSpecifiedSkill2Talent(talent));
    const core = buildCoreAnalytics(allTalents);
    const salesStaffRanking = demoUsers
        .filter((user) => user.role === 'Sales')
        .map((salesUser) => {
            const staffTalents = talents.filter((talent) => talent.salesStaffId === salesUser.id);
            return {
                salesStaffId: salesUser.id,
                salesStaff: salesUser.username,
                employedTalents: staffTalents.filter((talent) => talent.employmentStatus === '在職').length,
                pendingTalents: staffTalents.filter((talent) => talent.employmentStatus === '内定').length,
                applyingTalents: staffTalents.filter((talent) => talent.employmentStatus === '申請中').length,
                childcareLeaveTalents: staffTalents.filter((talent) => talent.employmentPauseType === '育休').length,
                awaitingEntryTalents: staffTalents.filter((talent) => talent.employmentStatus === '待入国').length,
                totalTalents: staffTalents.length,
            };
        });

    return {
        ...core,
        salesStaffRanking,
        attritionStats: {
            overallRate: 7.8,
            resignedCount: talents.filter((talent) => talent.employmentStatus === '離職').length,
            populationCount: talents.length,
            byNationality: distribution(talents, (talent) => talent.nationality).map((item, index) => ({
                nationality: item.label,
                resignedCount: index % 3,
                totalCount: item.count,
            })),
            byField: distribution(talents, (talent) => talent.field).map((item, index) => ({
                field: item.label,
                resignedCount: index % 2,
                totalCount: item.count,
            })),
        },
    };
};

const buildDashboardSummary = (sourceTalents) => {
    const allTalents = decorateTalents(sourceTalents);
    const specifiedSkill2Stats = buildSpecifiedSkill2Stats(allTalents);
    const talents = allTalents.filter((talent) => !isSpecifiedSkill2Talent(talent));
    const months = monthsBack(6);
    const statusDistribution = distribution(talents, (talent) => talent.employmentStatus, 'status');
    const nationalityFieldTree = distribution(talents, (talent) => talent.nationality, 'nationality').map((nationalityBucket) => ({
        nationality: nationalityBucket.nationality,
        count: nationalityBucket.count,
        fields: distribution(
            talents.filter((talent) => talent.nationality === nationalityBucket.nationality),
            (talent) => talent.field,
            'field'
        ),
    }));

    return {
        totalCount: talents.length,
        statusDistribution,
        statusFunnel: statusDistribution,
        nationalityFieldTree,
        salesTimeline: {
            mode: 'perSales',
            months,
            maxValue: 10,
            series: demoUsers.filter((user) => user.role === 'Sales').map((user, index) => ({
                salesStaff: user.username,
                label: user.username,
                counts: months.map((_, monthIndex) => 2 + ((index + monthIndex) % 5)),
                total: talents.filter((talent) => talent.salesStaffId === user.id).length,
            })),
        },
        specifiedSkill2Stats,
        generatedAt: new Date().toISOString(),
    };
};

const escapePdfText = (value = '') => String(value)
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
    .replace(/[^\x20-\x7E]/g, '?');

const makeDemoPdfBlob = (label = 'Demo Attachment') => {
    const safeLabel = escapePdfText(label);
    const content = [
        'BT',
        '/F1 24 Tf',
        '72 760 Td',
        `(${safeLabel}) Tj`,
        '0 -32 Td',
        '/F1 14 Tf',
        '(Demo PDF Preview) Tj',
        '0 -24 Td',
        '(Online preview worker is configured.) Tj',
        'ET'
    ].join('\n');

    const objects = [
        '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj',
        '2 0 obj\n<< /Type /Pages /Count 1 /Kids [3 0 R] >>\nendobj',
        '3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>\nendobj',
        `4 0 obj\n<< /Length ${content.length} >>\nstream\n${content}\nendstream\nendobj`,
        '5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj'
    ];

    let pdf = '%PDF-1.4\n';
    const offsets = objects.map((object) => {
        const offset = pdf.length;
        pdf += `${object}\n`;
        return offset;
    });
    const xrefOffset = pdf.length;

    pdf += `xref\n0 ${objects.length + 1}\n`;
    pdf += '0000000000 65535 f \n';
    offsets.forEach((offset) => {
        pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
    });
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

    return new Blob([pdf], { type: 'application/pdf' });
};

const makeDemoBlob = (label = 'Demo Attachment', fileType = 'image/svg+xml') => {
    if (String(fileType).toLowerCase().includes('pdf')) {
        return makeDemoPdfBlob(label);
    }

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420"><rect width="640" height="420" fill="#eef2ff"/><rect x="40" y="40" width="560" height="340" rx="24" fill="#ffffff" stroke="#c7d2fe" stroke-width="3"/><text x="320" y="190" text-anchor="middle" font-family="Arial" font-size="34" fill="#3730a3">${label}</text><text x="320" y="238" text-anchor="middle" font-family="Arial" font-size="18" fill="#64748b">Static demo preview</text></svg>`;
    return new Blob([svg], { type: 'image/svg+xml' });
};

const findLoan = (state, loanId) => state.loans.find((loan) => loan.id === loanId);

let jobSeekerRegistrations = [
    {
        id: '1792047600000-11111111-1111-4111-8111-111111111111',
        submittedAt: '2026-10-15T07:00:00.000Z',
        fields: {
            registration_date: '2026-10-15', family_name: 'DEMO', given_name: 'AN',
            full_name: 'DEMO AN', nationality: 'ベトナム', age: 28,
            email: 'demo.an@example.test', contact_methods: ['メール', '電話'],
            desired_job_category: '介護', desired_job: '訪問介護',
            current_salary: ['200万〜250万円'], desired_salary: ['250万〜300万円'],
            experiences: [{ company: 'デモ介護施設', start: '2022-04', current: true, employment_type: '正社員', duties: '生活支援' }],
            language_skills: [{ language: '日本語', certificate: 'JLPT N3', level: '' }],
            privacy_consent: 'agreed', self_pr: '介護の経験があります。\n日本語を勉強しています。',
        },
        attachments: [{ kind: 'resume', name: 'デモ履歴書.txt', size: 40 }, { kind: 'history', name: 'デモ職務経歴書.txt', size: 40 }],
        notificationDelivered: true, receiptDelivered: true,
    },
    {
        id: '1791961200000-22222222-2222-4222-8222-222222222222',
        submittedAt: '2026-10-14T07:00:00.000Z',
        fields: {
            family_name: 'DEMO', given_name: 'LIN', full_name: 'DEMO LIN', nationality: 'ミャンマー',
            email: 'demo.lin@example.test', contact_methods: ['メール'],
            desired_job: '食品工場での製造業務', current_salary: '250', desired_salary: '300',
            resume_later: 'yes', privacy_consent: 'agreed',
        },
    },
];

export const demoApiService = {
    getJobSeekerRegistrations: async ({ page = 1, pageSize = 20 } = {}) => ({
        items: [...jobSeekerRegistrations].sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))
            .slice((page - 1) * pageSize, page * pageSize).map(record => ({
                id: record.id, submittedAt: record.submittedAt, fullName: record.fields.full_name,
                nationality: record.fields.nationality, desiredJobCategory: record.fields.desired_job_category,
                desiredJob: record.fields.desired_job, contactMethods: record.fields.contact_methods,
                attachments: record.attachments || [], notificationDelivered: record.notificationDelivered ?? null,
                receiptDelivered: record.receiptDelivered ?? null,
            })),
        total: jobSeekerRegistrations.length, page, pageSize,
    }),
    getJobSeekerRegistration: async (id) => {
        const record = jobSeekerRegistrations.find(item => item.id === id);
        if (!record) throw new Error('登録が見つかりません。');
        return record;
    },
    downloadJobSeekerAttachment: async (id, kind) => {
        const file = jobSeekerRegistrations.find(item => item.id === id)?.attachments?.find(item => item.kind === kind);
        if (!file) throw new Error('書類が見つかりません。');
        return { blob: new Blob(['デモ用の架空の書類です。'], { type: 'text/plain;charset=utf-8' }), fileName: file.name || '' };
    },
    deleteJobSeekerRegistration: async (id) => {
        if (!jobSeekerRegistrations.some(item => item.id === id)) throw new Error('登録が見つかりません。');
        jobSeekerRegistrations = jobSeekerRegistrations.filter(item => item.id !== id);
    },

    TokenManager,

    initializeSharedState,

    login: async (username, password) => {
        const user = demoUsers.find((item) => item.username === username);
        if (!user || password !== DEMO_PASSWORD) {
            throw new Error('Demo login failed');
        }

        await initializeSharedState();

        const accessToken = createFakeJwt(user);
        const refreshToken = `demo-refresh-${user.id}`;
        TokenManager.setTokens(accessToken, refreshToken);
        return delay({
            accessToken,
            refreshToken,
            expiresIn: 86400,
            tokenType: 'Bearer',
        });
    },

    logout: async () => {
        TokenManager.clearTokens();
        return delay({ success: true });
    },

    getTalents: async (params = {}) => {
        const state = loadState();
        const visible = getVisibleTalents(state);
        const filtered = applyTalentFilters(visible, params);
        const sorted = sortTalents(filtered, params);
        const totals = calculateFeeTotals(sorted);
        if (params.noPagination) {
            return delay({ data: decorateTalents(sorted), currentPage: 1, totalPages: 1, totalCount: sorted.length, pageSize: sorted.length, totals });
        }
        const paginated = paginate(sorted, params);
        return delay({ ...paginated, data: decorateTalents(paginated.data), totals });
    },

    getTalentById: async (id) => {
        const state = loadState();
        const talent = state.talents.find((item) => item.id === id);
        if (!talent) throw new Error('Talent not found');
        return delay(decorateTalent(talent));
    },

    createTalent: async (talentData) => {
        const state = updateState((draft) => {
            const user = getCurrentUser();
            const salesStaff = draft.users.find((item) => item.id === (talentData.salesStaffId || user.id)) || draft.users.find((item) => item.role === 'Sales');
            const id = createId('demo-talent');
            const now = new Date().toISOString();
            const talent = {
                id,
                salesStaffId: salesStaff.id,
                salesStaff,
                managementNumber: talentData.managementNumber || `TM-DEMO-${draft.talents.length + 1}`,
                fullName: talentData.fullName || 'Demo Talent',
                fullNameFurigana: talentData.fullNameFurigana || '',
                gender: talentData.gender || '',
                dateOfBirth: talentData.dateOfBirth || null,
                nationality: talentData.nationality || '',
                field: talentData.field || '',
                address: talentData.address || '',
                phoneNumber: talentData.phoneNumber || '',
                employmentStatus: talentData.employmentStatus || '内定',
                initialCost: talentData.initialCost ?? null,
                initialCostNotCharged: Boolean(talentData.initialCostNotCharged),
                managementFee: talentData.managementFee ?? 0,
                managementFeeNotCharged: Boolean(talentData.managementFeeNotCharged),
                firstMonthManagementFee: talentData.firstMonthManagementFee ?? null,
                billingPlanType: talentData.billingPlanType || '通常',
                createdAt: now,
                updatedAt: now,
                residencePermit: null,
                activeLoanBorrowedAmount: 0,
                activeLoanRentalRepaidAmount: 0,
                activeLoanAdvanceRepaidAmount: 0,
                activeLoanRepaidAmount: 0,
                activeLoanRemainingAmount: 0,
                loans: [],
                ...talentData,
            };
            draft.talents.unshift(talent);
            return draft;
        });
        return delay(decorateTalent(state.talents[0]));
    },

    updateTalent: async (id, talentData) => {
        const state = updateState((draft) => {
            const index = draft.talents.findIndex((talent) => talent.id === id);
            if (index >= 0) {
                const currentTalent = draft.talents[index];
                const isSpecifiedSkill2 = isSpecifiedSkill2Talent(currentTalent);
                draft.talents[index] = {
                    ...currentTalent,
                    ...talentData,
                    ...(isSpecifiedSkill2 ? {
                        initialCost: currentTalent.initialCost,
                        initialCostNotCharged: currentTalent.initialCostNotCharged,
                        managementFee: currentTalent.managementFee,
                        managementFeeNotCharged: currentTalent.managementFeeNotCharged,
                        firstMonthManagementFee: currentTalent.firstMonthManagementFee,
                        billingPlanType: currentTalent.billingPlanType,
                        includedManagementMonths: currentTalent.includedManagementMonths,
                        tier1Months: currentTalent.tier1Months,
                        tier1ManagementFee: currentTalent.tier1ManagementFee,
                        tier2Months: currentTalent.tier2Months,
                        tier2ManagementFee: currentTalent.tier2ManagementFee,
                        introductionFee: talentData.introductionFee ?? currentTalent.introductionFee ?? null,
                    } : {}),
                    updatedAt: new Date().toISOString(),
                };
            }
            return draft;
        });
        return delay(decorateTalent(state.talents.find((talent) => talent.id === id)));
    },

    deleteTalent: async (id) => {
        updateState((draft) => {
            draft.talents = draft.talents.filter((talent) => talent.id !== id);
            draft.residences = draft.residences.filter((residence) => residence.talentId !== id);
            draft.loans = draft.loans.filter((loan) => loan.talentId !== id);
            draft.attachments = draft.attachments.filter((attachment) => attachment.talentId !== id);
            return draft;
        });
        return delay(null);
    },

    getTalentMapData: async () => delay(decorateTalents(getVisibleTalents(loadState()))),
    getDashboardSummary: async () => delay(buildDashboardSummary(getVisibleTalents(loadState()))),
    getAdminAnalytics: async () => delay(buildAdminAnalytics(getVisibleTalents(loadState()))),
    getSalesAnalytics: async () => delay(buildCoreAnalytics(getVisibleTalents(loadState()))),

    getAccountingAnalytics: async () => {
        const state = loadState();
        const loans = state.loans;
        const allEntries = loans.flatMap(getActiveLoanEntries);
        const totalLoanAmount = allEntries.reduce((sum, entry) => sum + toNumber(entry.amount), 0);
        const rentalLoanAmount = allEntries
            .filter(isRentalLoanEntry)
            .reduce((sum, entry) => sum + toNumber(entry.amount), 0);
        const advanceLoanAmount = totalLoanAmount - rentalLoanAmount;
        const totalRepaymentAmount = loans.reduce((sum, loan) => sum + loan.repayments.reduce((repaymentSum, repayment) => repaymentSum + toNumber(repayment.amount), 0), 0);
        const remainingBalance = loans.reduce((sum, loan) => sum + getLoanFinancialSummary(loan).totalRemaining, 0);
        const months = monthsBack(6);
        return delay({
            totalLoanAmount,
            rentalLoanAmount,
            advanceLoanAmount,
            totalRepaymentAmount,
            remainingBalance,
            totalBorrowers: loans.length,
            fullyRepaidCount: loans.filter((loan) => getLoanFinancialSummary(loan).totalRemaining <= 0).length,
            unrepaidCount: loans.filter((loan) => getLoanFinancialSummary(loan).totalRemaining > 0).length,
            averageLoanAmount: loans.length ? Math.round(totalLoanAmount / loans.length) : 0,
            monthlyLoanTrend: months.map((month, index) => ({ month, amount: 240000 + index * 50000 })),
            monthlyRepaymentTrend: months.map((month, index) => ({ month, amount: 100000 + index * 30000 })),
            repaymentStatusDistribution: [
                { status: '完全返済済み', count: loans.filter((loan) => getLoanFinancialSummary(loan).totalRemaining <= 0).length },
                { status: '未完済', count: loans.filter((loan) => getLoanFinancialSummary(loan).totalRemaining > 0).length },
            ],
            categoryDistribution: [
                { category: LOAN_ENTRY_CATEGORY.RENTAL, amount: rentalLoanAmount, count: allEntries.filter(isRentalLoanEntry).length },
                { category: '立替金', amount: advanceLoanAmount, count: allEntries.filter((entry) => !isRentalLoanEntry(entry)).length },
            ],
            organizationDistribution: distribution(state.talents, (talent) => talent.affiliatedOrganizationName, 'organization'),
            salesStaffDistribution: distribution(state.talents, (talent) => talent.salesStaff?.username, 'salesStaff'),
            monthlyLoanStats: months.map((month, index) => ({ month, amount: 220000 + index * 42000 })),
            monthlyRepaymentStats: months.map((month, index) => ({ month, amount: 90000 + index * 25000 })),
            quarterlyStats: ['Q1', 'Q2', 'Q3', 'Q4'].map((quarter, index) => ({
                quarter,
                loanAmount: 450000 + index * 130000,
                repaymentAmount: 190000 + index * 90000,
            })),
        });
    },

    getResidenceByTalentId: async (talentId) => {
        const state = loadState();
        return delay(state.residences.find((residence) => residence.talentId === talentId) || null);
    },

    createOrUpdateResidence: async (talentId, data) => {
        const state = updateState((draft) => {
            const existingIndex = draft.residences.findIndex((residence) => residence.talentId === talentId);
            const residence = {
                id: data.id || `demo-residence-${talentId}`,
                talentId,
                residenceStaffId: 'demo-user-residence',
                ...data,
                updatedAt: new Date().toISOString(),
            };
            if (existingIndex >= 0) {
                draft.residences[existingIndex] = residence;
            } else {
                draft.residences.push(residence);
            }
            const talent = draft.talents.find((item) => item.id === talentId);
            if (talent) {
                talent.residencePermit = residence;
                talent.currentResidenceStatus = getCurrentResidenceStatus(residence);
                talent.isSpecifiedSkill2 = isSpecifiedSkill2Status(talent.currentResidenceStatus);
            }
            return draft;
        });
        return delay(state.residences.find((residence) => residence.talentId === talentId));
    },

    getLoanByTalentId: async (talentId) => {
        const state = loadState();
        const loan = state.loans.find((item) => item.talentId === talentId && item.status === 'Active') || state.loans.find((item) => item.talentId === talentId);
        if (!loan) throw new Error('Loan not found');
        return delay(cloneLoanWithActiveEntries(loan));
    },

    getAllLoansByTalentId: async (talentId) => delay(loadState().loans.filter((loan) => loan.talentId === talentId).map(cloneLoanWithActiveEntries)),

    completeLoan: async (talentId) => {
        updateState((draft) => {
            draft.loans.filter((loan) => loan.talentId === talentId).forEach((loan) => {
                loan.status = 'Completed';
                loan.remainingBalance = 0;
                loan.completedAt = new Date().toISOString();
                syncTalentLoanAggregates(draft, loan);
            });
            return draft;
        });
        return delay({ success: true });
    },

    createOrUpdateLoan: async (talentId, data) => {
        const state = updateState((draft) => {
            const existing = draft.loans.find((loan) => loan.talentId === talentId);
            if (existing) Object.assign(existing, data, { updatedAt: new Date().toISOString() });
            return draft;
        });
        return delay(state.loans.find((loan) => loan.talentId === talentId));
    },

    getLoanEntryCategories: async () => {
        const state = loadState();
        const categories = Array.isArray(state.loanEntryCategories)
            ? state.loanEntryCategories
            : createDefaultLoanEntryCategories();

        return delay(categories
            .filter(category => category.isActive !== false)
            .sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100)));
    },

    createLoanEntryCategory: async (name) => {
        const categoryName = String(name ?? '').trim();
        if (!categoryName) throw new Error('分類名を入力してください。');
        if (categoryName === LOAN_ENTRY_CATEGORY.RENTAL) throw new Error('賃貸はシステム分類のため追加できません。');

        const state = updateState((draft) => {
            draft.loanEntryCategories = Array.isArray(draft.loanEntryCategories)
                ? draft.loanEntryCategories
                : createDefaultLoanEntryCategories();

            const normalizedName = normalizeCategoryNameForStorage(categoryName);
            const existing = draft.loanEntryCategories.find(category => category.normalizedName === normalizedName);
            if (existing) {
                existing.isActive = true;
                existing.updatedAt = new Date().toISOString();
                return draft;
            }

            const maxSortOrder = draft.loanEntryCategories.reduce((max, category) => Math.max(max, category.sortOrder ?? 0), 0);
            draft.loanEntryCategories.push({
                id: createId('demo-loan-entry-category'),
                name: categoryName,
                normalizedName,
                isDefault: false,
                isActive: true,
                sortOrder: Math.max(maxSortOrder + 1, 100),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            });
            return draft;
        });

        const normalizedName = normalizeCategoryNameForStorage(categoryName);
        return delay(state.loanEntryCategories.find(category => category.normalizedName === normalizedName));
    },

    addLoanEntry: async (talentId, data) => {
        const state = updateState((draft) => {
            let loan = draft.loans.find((item) => item.talentId === talentId && item.status === 'Active');
            if (!loan) {
                loan = makeLoan(talentId, draft.loans.length + 1, data.loanDate || new Date().toISOString().slice(0, 10), 0);
                loan.loanEntries = [];
                loan.repayments = [];
                loan.status = 'Active';
                loan.completedAt = null;
                draft.loans.push(loan);
            }
            const entry = {
                id: createId('demo-loan-entry'),
                loanId: loan.id,
                ...data,
                category: normalizeLoanEntryCategory(data.category),
                subjectType: normalizeLoanSubjectType(data.subjectType),
                isDeleted: false,
            };
            loan.loanEntries.push(entry);
            recalculateDemoLoanAggregates(loan);
            syncTalentLoanAggregates(draft, loan);
            return draft;
        });
        const loan = state.loans.find((item) => item.talentId === talentId && item.status === 'Active');
        return delay(getActiveLoanEntries(loan).slice(-1)[0]);
    },

    updateLoanEntry: async (talentId, entryId, data) => {
        const state = updateState((draft) => {
            const loan = draft.loans.find((item) => item.talentId === talentId && item.loanEntries?.some(entry => entry.id === entryId));
            const entry = loan?.loanEntries.find((item) => item.id === entryId && !item.isDeleted);
            if (entry && loan) {
                Object.assign(entry, data, {
                    category: normalizeLoanEntryCategory(data.category),
                    subjectType: normalizeLoanSubjectType(data.subjectType ?? entry.subjectType),
                    isDeleted: false,
                });
                recalculateDemoLoanAggregates(loan);
                syncTalentLoanAggregates(draft, loan);
            }
            return draft;
        });
        const loan = state.loans.find((item) => item.talentId === talentId && item.loanEntries?.some(entry => entry.id === entryId));
        return delay(getActiveLoanEntries(loan).find((item) => item.id === entryId) || null);
    },

    deleteLoanEntry: async (talentId, entryId) => {
        updateState((draft) => {
            const loan = draft.loans.find((item) => item.talentId === talentId && item.loanEntries?.some(entry => entry.id === entryId));
            if (loan) {
                const entry = loan.loanEntries.find((item) => item.id === entryId);
                if (entry) entry.isDeleted = true;
                draft.attachments.forEach((attachment) => {
                    if (attachment.loanEntryId === entryId) attachment.loanEntryId = null;
                });
                recalculateDemoLoanAggregates(loan);
                syncTalentLoanAggregates(draft, loan);
            }
            return draft;
        });
        return delay(null);
    },

    addRepayment: async (loanId, repaymentData) => {
        const currentLoan = findLoan(loadState(), loanId);
        if (currentLoan) {
            const category = repaymentData.category || REPAYMENT_CATEGORY.ADVANCE;
            const normalizedSubjectType = normalizeLoanSubjectType(repaymentData.subjectType);
            if (category !== REPAYMENT_CATEGORY.RENTAL) {
                const subjectKey = getSubjectKey(normalizedSubjectType);
                const remainingAmount = getLoanFinancialSummary(currentLoan).subjectTotals[subjectKey].advanceRemaining;
                if (toNumber(repaymentData.amount) > remainingAmount) {
                    throw new Error('選択した返済主体の立替金残高を超えています。');
                }
            }
        }
        const state = updateState((draft) => {
            const loan = findLoan(draft, loanId);
            if (loan) {
                const category = repaymentData.category || REPAYMENT_CATEGORY.ADVANCE;
                const loanEntryId = repaymentData.loanEntryId || null;
                const rentalEntry = category === REPAYMENT_CATEGORY.RENTAL
                    ? getActiveLoanEntries(loan).find(entry => entry.id === loanEntryId)
                    : null;
                const alreadyPaid = category === REPAYMENT_CATEGORY.RENTAL
                    && loan.repayments.some(repayment => repayment.loanEntryId === loanEntryId);
                if (alreadyPaid) return draft;
                loan.repayments.push({
                    id: createId('demo-repayment'),
                    loanId,
                    ...repaymentData,
                    category,
                    subjectType: category === REPAYMENT_CATEGORY.RENTAL
                        ? normalizeLoanSubjectType(rentalEntry?.subjectType)
                        : normalizeLoanSubjectType(repaymentData.subjectType),
                    loanEntryId: category === REPAYMENT_CATEGORY.RENTAL ? loanEntryId : null,
                    amount: category === REPAYMENT_CATEGORY.RENTAL ? toNumber(rentalEntry?.amount) : toNumber(repaymentData.amount),
                });
                recalculateDemoLoanAggregates(loan);
                syncTalentLoanAggregates(draft, loan);
            }
            return draft;
        });
        return delay(findLoan(state, loanId));
    },

    updateRepayment: async (loanId, repaymentId, repaymentData) => {
        const currentLoan = findLoan(loadState(), loanId);
        const currentRepayment = currentLoan?.repayments?.find((item) => item.id === repaymentId);
        if (currentLoan && currentRepayment) {
            const category = repaymentData.category || currentRepayment.category || REPAYMENT_CATEGORY.ADVANCE;
            const normalizedSubjectType = normalizeLoanSubjectType(repaymentData.subjectType ?? currentRepayment.subjectType);
            if (category !== REPAYMENT_CATEGORY.RENTAL) {
                const nextRepayments = currentLoan.repayments.filter((item) => item.id !== repaymentId);
                const previewLoan = { ...currentLoan, repayments: nextRepayments };
                const subjectKey = getSubjectKey(normalizedSubjectType);
                const remainingAmount = getLoanFinancialSummary(previewLoan).subjectTotals[subjectKey].advanceRemaining;
                if (toNumber(repaymentData.amount) > remainingAmount) {
                    throw new Error('選択した返済主体の立替金残高を超えています。');
                }
            }
        }
        updateState((draft) => {
            const loan = findLoan(draft, loanId);
            const repayment = loan?.repayments.find((item) => item.id === repaymentId);
            if (repayment && loan) {
                const category = repaymentData.category || repayment.category || REPAYMENT_CATEGORY.ADVANCE;
                const loanEntryId = repaymentData.loanEntryId || repayment.loanEntryId || null;
                const rentalEntry = category === REPAYMENT_CATEGORY.RENTAL
                    ? getActiveLoanEntries(loan).find(entry => entry.id === loanEntryId)
                    : null;
                Object.assign(repayment, repaymentData, {
                    category,
                    subjectType: category === REPAYMENT_CATEGORY.RENTAL
                        ? normalizeLoanSubjectType(rentalEntry?.subjectType)
                        : normalizeLoanSubjectType(repaymentData.subjectType ?? repayment.subjectType),
                    loanEntryId: category === REPAYMENT_CATEGORY.RENTAL ? loanEntryId : null,
                    amount: category === REPAYMENT_CATEGORY.RENTAL ? toNumber(rentalEntry?.amount) : toNumber(repaymentData.amount),
                });
                recalculateDemoLoanAggregates(loan);
                syncTalentLoanAggregates(draft, loan);
            }
            return draft;
        });
        return delay({ success: true });
    },

    deleteRepayment: async (loanId, repaymentId) => {
        updateState((draft) => {
            const loan = findLoan(draft, loanId);
            if (loan) {
                loan.repayments = loan.repayments.filter((repayment) => repayment.id !== repaymentId);
                recalculateDemoLoanAggregates(loan);
                syncTalentLoanAggregates(draft, loan);
            }
            return draft;
        });
        return delay(null);
    },

    getAttachmentsForTalent: async (talentId) => delay(loadState().attachments.filter((attachment) => attachment.talentId === talentId)),
    uploadAttachment: async (talentId, file, sourceModule, loanEntryId = null) => {
        const state = updateState((draft) => {
            draft.attachments.push({
                id: createId('demo-attachment'),
                talentId,
                fileName: file?.name || 'demo-upload.txt',
                filePath: `demo://attachments/${talentId}/${file?.name || 'demo-upload.txt'}`,
                fileType: file?.type || 'application/octet-stream',
                fileSize: file?.size || 0,
                sourceModule,
                loanEntryId,
                createdAt: new Date().toISOString(),
            });
            return draft;
        });
        return delay(state.attachments[state.attachments.length - 1]);
    },
    downloadAttachment: async (attachmentId) => {
        const attachment = loadState().attachments.find((item) => item.id === attachmentId);
        return delay(makeDemoBlob(attachment?.fileName || attachmentId, attachment?.fileType), 60);
    },
    checkDuplicatePhotos: async () => delay({ totalDuplicateFiles: 0, totalAffectedRecords: 0, duplicates: [] }),
    deleteAttachment: async (attachmentId) => {
        updateState((draft) => {
            draft.attachments = draft.attachments.filter((attachment) => attachment.id !== attachmentId);
            return draft;
        });
        return delay(null);
    },
    cleanupOrphanedAttachments: async () => delay({ message: 'Demo cleanup completed', cleanedCount: 0 }),

    getAuditLogs: async () => delay([
        { id: 'demo-log-1', username: 'demo_admin', userRole: 'SuperAdmin', timestamp: new Date().toISOString(), actionType: 'ログイン', entityName: 'システム', description: 'デモ環境にログインしました' },
        { id: 'demo-log-2', username: 'demo_sales', userRole: 'Sales', timestamp: addDays(new Date(), -1), actionType: '更新', entityName: '人材情報', description: 'デモ人材情報を更新しました' },
    ]),

    recognizeResidencePermit: async () => delay({
        residenceCardNumber: 'AB12345678',
        residenceCardExpiry: addMonths(new Date(), 24),
        passportNumber: 'P7654321',
        passportExpiry: addMonths(new Date(), 36),
        confidence: 0.94,
        extractedText: 'Demo OCR result',
    }),

    getSalesUsers: async () => delay(loadState().users.filter((user) => user.role === 'Sales')),
    transferTalent: async (talentId, newSalesStaffId) => {
        const state = updateState((draft) => {
            const talent = draft.talents.find((item) => item.id === talentId);
            const salesStaff = draft.users.find((user) => user.id === newSalesStaffId);
            if (talent && salesStaff) {
                talent.salesStaffId = salesStaff.id;
                talent.salesStaff = salesStaff;
            }
            return draft;
        });
        return delay(state.talents.find((talent) => talent.id === talentId));
    },

    getClientOptions: async () => delay(loadState().clients),
    getClientOrganizations: async ({ search } = {}) => {
        const organizations = loadState().clients;
        if (!search) return delay(organizations);
        return delay(organizations.filter((organization) => organization.name.includes(search)));
    },
    getClientOrganization: async (organizationId) => delay(loadState().clients.find((organization) => organization.id === organizationId)),
    getClientOrganizationTalents: async (organizationId, options = {}) => {
        const talents = loadState().talents.filter((talent) => talent.clientOrganizationId === organizationId);
        return delay(paginate(talents, { page: options.page || 1, pageSize: options.pageSize || 10 }));
    },
    syncClientsFromTalents: async () => delay({ syncedOrganizations: 3, syncedWorkplaces: 6 }),
    createClientOrganization: async (payload) => {
        const state = updateState((draft) => {
            draft.clients.push({ id: createId('demo-client'), ...payload, workplaces: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
            return draft;
        });
        return delay(state.clients[state.clients.length - 1]);
    },
    updateClientOrganization: async (organizationId, payload) => {
        const state = updateState((draft) => {
            const organization = draft.clients.find((item) => item.id === organizationId);
            if (organization) Object.assign(organization, payload, { updatedAt: new Date().toISOString() });
            return draft;
        });
        return delay(state.clients.find((item) => item.id === organizationId));
    },
    deleteClientOrganization: async (organizationId) => {
        updateState((draft) => {
            draft.clients = draft.clients.filter((organization) => organization.id !== organizationId);
            return draft;
        });
        return delay(null);
    },
    createClientWorkplace: async (organizationId, payload) => {
        const state = updateState((draft) => {
            const organization = draft.clients.find((item) => item.id === organizationId);
            if (organization) organization.workplaces.push({ id: createId('demo-workplace'), organizationId, ...payload });
            return draft;
        });
        return delay(state.clients.find((item) => item.id === organizationId));
    },
    updateClientWorkplace: async (workplaceId, payload) => {
        updateState((draft) => {
            draft.clients.forEach((organization) => {
                const workplace = organization.workplaces.find((item) => item.id === workplaceId);
                if (workplace) Object.assign(workplace, payload);
            });
            return draft;
        });
        return delay({ success: true });
    },
    deleteClientWorkplace: async (workplaceId) => {
        updateState((draft) => {
            draft.clients.forEach((organization) => {
                organization.workplaces = organization.workplaces.filter((workplace) => workplace.id !== workplaceId);
            });
            return draft;
        });
        return delay(null);
    },
    getOrganizationContracts: async (organizationId) => delay(loadState().contracts.filter((contract) => contract.ownerType === 'organization' && contract.ownerId === organizationId)),
    uploadOrganizationContracts: async () => delay({ success: true }),
    getWorkplaceContracts: async (workplaceId) => delay(loadState().contracts.filter((contract) => contract.ownerType === 'workplace' && contract.ownerId === workplaceId)),
    uploadWorkplaceContracts: async () => delay({ success: true }),
    getTalentContracts: async (talentId) => delay(loadState().contracts.filter((contract) => contract.ownerType === 'talent' && contract.ownerId === talentId)),
    uploadTalentContracts: async () => delay({ success: true }),
    downloadClientContract: async (contractId) => {
        const contract = loadState().contracts.find((item) => item.id === contractId);
        return delay(makeDemoBlob(contract?.fileName || contractId, contract?.fileType), 60);
    },
    deleteClientContract: async (contractId) => {
        updateState((draft) => {
            draft.contracts = draft.contracts.filter((contract) => contract.id !== contractId);
            return draft;
        });
        return delay(null);
    },

    getAllUsersForManagement: async () => delay(loadState().users),
    createUser: async (username, password, role) => {
        if (!password) throw new Error('Password is required');
        const state = updateState((draft) => {
            draft.users.push({ id: createId('demo-user'), username, role });
            return draft;
        });
        return delay(state.users[state.users.length - 1]);
    },
    updateUsername: async (userId, newUsername) => {
        updateState((draft) => {
            const user = draft.users.find((item) => item.id === userId);
            if (user) user.username = newUsername;
            return draft;
        });
        return delay({ message: 'updated' });
    },
    resetUserPassword: async () => delay({ message: 'Demo password reset completed' }),
    deleteUserEnhanced: async (userId) => {
        updateState((draft) => {
            draft.users = draft.users.filter((user) => user.id !== userId);
            return draft;
        });
        return delay(null);
    },

    getSystemNotifications: async () => delay(loadState().notifications),
    createSystemNotification: async (payload) => {
        const state = updateState((draft) => {
            draft.notifications.unshift({
                id: createId('demo-notification'),
                ...payload,
                status: 'Draft',
                createdAt: new Date().toISOString(),
                createdBy: getCurrentUser().id,
                createdByName: getCurrentUser().username,
                isRead: false,
            });
            return draft;
        });
        return delay(state.notifications[0]);
    },
    updateSystemNotification: async (notificationId, payload) => {
        const state = updateState((draft) => {
            const notification = draft.notifications.find((item) => item.id === notificationId);
            if (notification) Object.assign(notification, payload);
            return draft;
        });
        return delay(state.notifications.find((item) => item.id === notificationId));
    },
    sendSystemNotification: async (notificationId) => {
        const state = updateState((draft) => {
            const notification = draft.notifications.find((item) => item.id === notificationId);
            if (notification) {
                notification.status = 'Sent';
                notification.sentTime = new Date().toISOString();
            }
            return draft;
        });
        return delay(state.notifications.find((item) => item.id === notificationId));
    },
    deleteSystemNotification: async (notificationId) => {
        updateState((draft) => {
            draft.notifications = draft.notifications.filter((notification) => notification.id !== notificationId);
            return draft;
        });
        return delay(null);
    },
    getMySystemNotifications: async () => {
        const user = getCurrentUser();
        const notifications = loadState().notifications.filter((notification) => (
            !notification.targetUserIds || notification.targetUserIds.includes(user.id) || user.role === 'SuperAdmin'
        ));
        return delay(notifications);
    },
    markSystemNotificationRead: async (notificationId) => {
        const state = updateState((draft) => {
            const notification = draft.notifications.find((item) => item.id === notificationId);
            if (notification) {
                notification.isRead = true;
                notification.readAt = new Date().toISOString();
            }
            return draft;
        });
        return delay(state.notifications.find((item) => item.id === notificationId));
    },

    getLatestSpecifiedSkillNews: async (limit = 8) => delay(loadState().news.slice(0, limit)),
    getSpecifiedSkillNews: async (options = {}) => {
        const state = loadState();
        let news = state.news.filter((article) => article.status === (options.status || 'Published'));
        if (options.category) {
            news = news.filter((article) => article.category === options.category);
        }
        return delay({
            data: news,
            currentPage: 1,
            totalPages: 1,
            totalCount: news.length,
            unreadCount: news.filter((article) => !article.isRead).length,
        });
    },
    markSpecifiedSkillNewsRead: async (articleId) => {
        const state = updateState((draft) => {
            const article = draft.news.find((item) => item.id === articleId);
            if (article) {
                article.isRead = true;
                article.readAt = new Date().toISOString();
            }
            return draft;
        });
        return delay(state.news.find((article) => article.id === articleId));
    },
    markAllSpecifiedSkillNewsRead: async () => {
        updateState((draft) => {
            draft.news.forEach((article) => {
                article.isRead = true;
                article.readAt = article.readAt || new Date().toISOString();
            });
            return draft;
        });
        return delay({ success: true });
    },
    archiveSpecifiedSkillNews: async (articleId) => {
        updateState((draft) => {
            const article = draft.news.find((item) => item.id === articleId);
            if (article) article.status = 'Archived';
            return draft;
        });
        return delay({ success: true });
    },
    restoreSpecifiedSkillNews: async (articleId) => {
        updateState((draft) => {
            const article = draft.news.find((item) => item.id === articleId);
            if (article) article.status = 'Published';
            return draft;
        });
        return delay({ success: true });
    },

    getTalentBillingSeries: async (id, months = 36) => {
        const state = loadState();
        const talent = state.talents.find((item) => item.id === id);
        const start = new Date();
        const series = Array.from({ length: Math.min(months, 36) }).map((_, index) => {
            const date = new Date(start);
            date.setMonth(start.getMonth() + index);
            return {
                month: date.toISOString().slice(0, 7),
                amount: talent?.managementFee || 0,
                baseAmount: talent?.managementFee || 0,
                overrideAmount: null,
                status: index < 3 ? 'Actual' : 'Forecast',
            };
        });
        return delay({ talentId: id, series });
    },
    getTalentBillingLedger: async (id) => delay({ talentId: id, entries: [] }),
    upsertTalentBillingOverride: async () => delay({ success: true }),
    restoreTalentBillingOverride: async () => delay({ success: true }),
    getFeeChanges: async (id) => delay(loadState().feeChanges[id] || []),
    addFeeChange: async (id, payload) => {
        const state = updateState((draft) => {
            draft.feeChanges[id] = draft.feeChanges[id] || [];
            draft.feeChanges[id].push({ id: createId('demo-fee-change'), talentId: id, ...payload, createdAt: new Date().toISOString() });
            return draft;
        });
        return delay(state.feeChanges[id]);
    },
    deleteFeeChange: async (id, changeId) => {
        updateState((draft) => {
            draft.feeChanges[id] = (draft.feeChanges[id] || []).filter((change) => change.id !== changeId);
            return draft;
        });
        return delay(null);
    },
    getTalentEmploymentPauses: async (id) => {
        const talent = loadState().talents.find((item) => item.id === id);
        if (!talent || !talent.employmentPauseStartDate) return delay({ pauses: [] });
        return delay({
            pauses: [
                {
                    id: `pause-${id}`,
                    pauseType: talent.employmentPauseType,
                    startDate: talent.employmentPauseStartDate,
                    endDate: talent.employmentPauseEndDate,
                },
            ],
        });
    },
    updateTalentEmploymentPauses: async () => delay({ success: true }),
    updateResidenceEmploymentDates: async (id, payload) => {
        const state = updateState((draft) => {
            const residence = draft.residences.find((item) => item.talentId === id);
            if (residence) Object.assign(residence, payload);
            return draft;
        });
        return delay(state.residences.find((item) => item.talentId === id));
    },
};
